import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const ProductForm = ({ product, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    expiry_date: '',
    description: '',
    image: '',
    additional_images_text: '',
    stock_level: 100
  });

  useEffect(() => {
    if (product) {
      setFormData({
        ...product,
        expiry_date: product.expiry_date ? product.expiry_date.split('T')[0] : '',
        additional_images_text: product.additional_images ? product.additional_images.join('\n') : ''
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'price' || name === 'stock_level') ? parseFloat(value) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Clean up empty expiry date before sending to DB
    const submitData = { ...formData };
    
    if (!submitData.expiry_date) {
      submitData.expiry_date = null;
    }

    // Process additional images from textarea
    delete submitData.additional_images_text;
    submitData.additional_images = formData.additional_images_text
      .split('\n')
      .map(url => url.trim())
      .filter(url => url !== '');

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md relative shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
        >
          <SafeIcon icon={FiX} className="w-6 h-6" />
        </button>
        
        <h2 className="text-2xl font-serif text-brand-darkSage mb-6">
          {product ? 'Edit Product' : 'Add New Product'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input 
              type="text" 
              name="name" 
              value={formData.name} 
              onChange={handleChange} 
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold focus:border-brand-gold outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (Rs.)</label>
              <input 
                type="number" 
                step="0.01"
                name="price" 
                value={formData.price} 
                onChange={handleChange} 
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock Level</label>
              <input 
                type="number" 
                name="stock_level" 
                value={formData.stock_level} 
                onChange={handleChange} 
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input 
                type="text" 
                name="category" 
                value={formData.category} 
                onChange={handleChange} 
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
              <input 
                type="date" 
                name="expiry_date" 
                value={formData.expiry_date} 
                onChange={handleChange} 
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Image URL</label>
            <input 
              type="url" 
              name="image" 
              value={formData.image} 
              onChange={handleChange} 
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Images (One URL per line)</label>
            <textarea 
              name="additional_images_text" 
              value={formData.additional_images_text} 
              onChange={handleChange} 
              rows="2"
              placeholder="https://...&#10;https://..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold outline-none resize-none text-xs"
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea 
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-brand-gold outline-none resize-none"
            ></textarea>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >Cancel</button>
            <button 
              type="submit"
              className="px-6 py-2 bg-brand-darkSage text-white rounded-lg hover:bg-brand-sage transition-colors"
            >
              {product ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;